// Gantt Stats Component - Statistics cards
import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, Clock, AlertTriangle, CheckCircle,
  Users, Calendar, Target, Activity
} from 'lucide-react';
import { GanttData } from '../types/gantt.types';

interface GanttStatsProps {
  stats: {
    totalTasks: number;
    completedTasks: number;
    delayedTasks: number;
    criticalTasks: number;
    overallProgress: number;
  };
  data: GanttData;
}

export const GanttStats: React.FC<GanttStatsProps> = ({ stats, data }) => {
  const cards = [
    {
      title: 'Wszystkie zadania',
      value: stats.totalTasks,
      icon: Activity,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/20',
      change: null
    },
    {
      title: 'Ukończone',
      value: stats.completedTasks,
      icon: CheckCircle,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/20',
      percentage: Math.round((stats.completedTasks / stats.totalTasks) * 100) || 0
    },
    {
      title: 'Opóźnione',
      value: stats.delayedTasks,
      icon: AlertTriangle,
      color: 'from-red-500 to-pink-500',
      bgColor: 'bg-red-500/20',
      percentage: Math.round((stats.delayedTasks / stats.totalTasks) * 100) || 0
    },
    {
      title: 'Ścieżka krytyczna',
      value: stats.criticalTasks,
      icon: Target,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-500/20',
      percentage: Math.round((stats.criticalTasks / stats.totalTasks) * 100) || 0
    },
    {
      title: 'Postęp ogólny',
      value: `${stats.overallProgress}%`,
      icon: TrendingUp,
      color: 'from-orange-500 to-red-500',
      bgColor: 'bg-orange-500/20',
      isProgress: true
    }
  ];

  return (
    <div className="px-6 py-4 bg-zinc-900/50">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {cards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-zinc-800 rounded-lg p-4 border border-zinc-700 hover:border-zinc-600 transition-all"
          >
            <div className="flex items-center justify-between mb-2">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${card.color}`}>
                <card.icon className="w-5 h-5 text-white" />
              </div>
              {card.percentage !== undefined && (
                <span className="text-xs text-gray-400">
                  {card.percentage}%
                </span>
              )}
            </div>
            <p className="text-2xl font-bold text-white">{card.value}</p>
            <p className="text-sm text-gray-400 mt-1">{card.title}</p>
            
            {card.isProgress && (
              <div className="mt-3">
                <div className="w-full bg-zinc-700 rounded-full h-1.5">
                  <div
                    className={`bg-gradient-to-r ${card.color} h-1.5 rounded-full transition-all`}
                    style={{ width: `${stats.overallProgress}%` }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};